/**
 * LifecycleTab Component
 *
 * Displays lifecycle configuration including states, transitions, and actions.
 */

import React from 'react'
import { Card, Tag, Space, Typography, Empty, Table, Descriptions, Alert, Tooltip } from 'antd'
import * as AntdIcons from '@ant-design/icons'
import {
  PlayCircleOutlined,
  StopOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
  SettingOutlined
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { TabProps, LifecycleAction } from '../types'

const { Text } = Typography

/**
 * Render an Ant Design icon by name string
 */
function renderIcon(iconName: string | undefined, style?: React.CSSProperties): React.ReactElement {
  if (!iconName) {
    return <QuestionCircleOutlined style={style} />
  }

  // Get icon component from @ant-design/icons
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const IconComponent = (AntdIcons as any)[iconName]

  if (IconComponent) {
    return <IconComponent style={style} />
  }

  // Fallback if icon not found
  return <QuestionCircleOutlined style={style} />
}

/**
 * State badge with color
 */
function StateBadge({ state }: { state: string }): React.ReactElement {
  // Color mapping for common states
  const stateColors: Record<string, string> = {
    draft: 'default',
    pending: 'processing',
    pending_approval: 'warning',
    active: 'success',
    approved: 'success',
    published: 'success',
    inactive: 'warning',
    expired: 'warning',
    superseded: 'default',
    suspended: 'warning',
    rejected: 'error',
    cancelled: 'error',
    deleted: 'error',
    archived: 'default'
  }

  const color = stateColors[state.toLowerCase()] || 'default'

  return <Tag color={color}>{state}</Tag>
}

/**
 * Action row for table display
 */
interface ActionRow {
  key: string
  actionKey: string
  action: LifecycleAction
}

export function LifecycleTab({ metadata }: TabProps): React.ReactElement {
  const lifecycle = metadata.lifecycle

  // No lifecycle management
  if (!lifecycle || !lifecycle.enabled) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Space direction="vertical" align="center">
            <StopOutlined style={{ fontSize: '32px', color: '#d9d9d9' }} />
            <Text type="secondary">No lifecycle management configured for this entity</Text>
          </Space>
        }
      />
    )
  }

  // Extract states from actions if not explicitly provided
  // Extract states from actions since LifecycleMetadata doesn't include states array
  const states = extractStatesFromActions(lifecycle.actions)

  // Table columns for actions - simple 3-column layout
  const actionColumns: ColumnsType<ActionRow> = [
    {
      title: 'Action',
      dataIndex: 'actionKey',
      key: 'action',
      render: (key: string, record: ActionRow) => (
        <Space>
          {renderIcon(record.action.icon, { color: record.action.danger ? '#ff4d4f' : '#1890ff' })}
          <Text strong>{record.action.label || key}</Text>
        </Space>
      )
    },
    {
      title: 'From States',
      key: 'fromStates',
      render: (_: any, record: ActionRow) => (
        <Space wrap>
          {record.action.fromStates.map((state) => (
            <StateBadge key={state} state={state} />
          ))}
        </Space>
      )
    },
    {
      title: 'To State',
      key: 'toState',
      render: (_: any, record: ActionRow) => (
        record.action.toState ? (
          <StateBadge state={record.action.toState} />
        ) : (
          <Tooltip title="Creates a new draft copy; original record unchanged">
            <Tag color="blue">New Draft</Tag>
          </Tooltip>
        )
      )
    }
  ]

  // Convert actions to table data
  const actionData: ActionRow[] = Object.entries(lifecycle.actions).map(([key, action]) => ({
    key,
    actionKey: key,
    action
  }))

  return (
    <div>
      {/* Overview */}
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Lifecycle Enabled">
            <Tag color="success" icon={<CheckCircleOutlined />}>
              Yes
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="State Field">
            <Text code>{lifecycle.stateField}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Number of States">
            <Text>{states.length}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Number of Actions">
            <Text>{Object.keys(lifecycle.actions).length}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* States */}
      <Card
        size="small"
        title={
          <Space>
            <PlayCircleOutlined />
            <span>States</span>
          </Space>
        }
        style={{ marginBottom: '16px' }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {states.map((state) => (
            <StateBadge key={state} state={state} />
          ))}
        </div>
      </Card>

      {/* Actions table */}
      <Card
        size="small"
        title={
          <Space>
            <SettingOutlined />
            <span>Actions</span>
          </Space>
        }
      >
        <Table
          columns={actionColumns}
          dataSource={actionData}
          size="small"
          pagination={false}
          rowClassName={(record) =>
            record.action.danger ? 'metadata-inspector-danger-row' : ''
          }
        />

        {/* Action details expansion */}
        {actionData.length > 0 && (
          <>
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Action Details
              </Text>
            </div>
            {actionData.map((row) => (
              <Alert
                key={row.key}
                type={row.action.danger ? 'warning' : 'info'}
                style={{ marginTop: '8px' }}
                message={
                  <Space>
                    {renderIcon(row.action.icon, { color: row.action.danger ? '#ff4d4f' : '#1890ff' })}
                    <Text strong>{row.action.label || row.actionKey}</Text>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={4}>
                    {row.action.confirmationMessage && (
                      <Text type="secondary">
                        <ExclamationCircleOutlined style={{ marginRight: '4px' }} />
                        Confirmation: "{row.action.confirmationMessage}"
                      </Text>
                    )}
                    {row.action.successMessage && (
                      <Text type="secondary">
                        <CheckCircleOutlined style={{ marginRight: '4px', color: '#52c41a' }} />
                        Success: "{row.action.successMessage}"
                      </Text>
                    )}
                  </Space>
                }
                showIcon={false}
              />
            ))}
          </>
        )}
      </Card>
    </div>
  )
}

/**
 * Extract unique states from action definitions
 */
function extractStatesFromActions(actions: Record<string, LifecycleAction>): string[] {
  const states = new Set<string>()

  for (const action of Object.values(actions)) {
    action.fromStates.forEach((state: string) => states.add(state))
    if (action.toState) {
      states.add(action.toState)
    }
  }

  return Array.from(states).sort()
}

export default LifecycleTab
